import React, { useState } from "react";
import { Pagination, Tabs } from "antd";
import { FiChevronRight } from "react-icons/fi";
import { useHistory, useLocation } from "react-router-dom";
import { useAgencyPaymentHistory } from "../../hooks/usePayment";
import { formatCurrency, parseApiAmount } from "../../utils/amountFormat";
import {
  influencerPaymentStatusClass,
  influencerPaymentStatusLabel,
} from "../../utils/influencerPaymentStatus";

const TABS = [
  { key: "all", label: "전체" },
  { key: "pending", label: "지급대기" },
  { key: "paid", label: "지급완료" },
];
export const AgencyPaymentHistory = () => {
  const history = useHistory();
  const location = useLocation();
  const campaignId = location.state?.campaignId ?? null;
  const [page, setPage] = useState(1);

  const [activeTab, setActiveTab] = useState("all");
  const status = activeTab === "all" ? null : activeTab;

  const { data, isLoading } = useAgencyPaymentHistory({
    campaignId,
    status,
  });

  const list = Array.isArray(data?.result) ? data?.result : [];
  const total = data?.total;

  const handleOpenDetail = (item) => {
    history.push({
      pathname: `/payments/agency-payments-history/${item.pay_report_id}`,
      state: item,
    });
  };

  const tabItems = TABS.map((tab) => ({
    key: tab.key,
    label: tab.label,
  }));

  return (
    <div className="w-full max-w-[1400px]">
      <h2 className="text-3xl font-bold text-[#111111] md:text-4xl">
        소속 크리에이터 정산 내역
      </h2>

      <Tabs
        activeKey={activeTab}
        items={tabItems}
        onChange={setActiveTab}
        className="w-full font-normal mb-5"
      />

      {list.length === 0 && !isLoading && (
        <div className="rounded-[22px] border border-dashed border-[#e7eaee] bg-[#fafbfc] px-6 py-12 text-center text-[15px] text-[#7a7f87]">
          정산 내역이 없습니다.
        </div>
      )}

      <div className="space-y-4">
        {list.map((item) => {
          const settlementAmount = parseApiAmount(item.settlement_amount);
          const taxAmount = parseApiAmount(item.tax_amount);
          const currency = item.currency ?? "KRW";
          const title = item.campaign_title ?? "캠페인";
          const nicknameLabel = item.nickname?.trim() || "—";

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleOpenDetail(item)}
              className="group w-full rounded-[22px] border border-[#e7eaee] bg-white text-left shadow-[0_10px_30px_rgba(17,24,39,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#cfd7e2] hover:shadow-[0_18px_44px_rgba(17,24,39,0.08)]"
            >
              <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:gap-5 lg:px-6 lg:py-4">
                <div className="min-w-0 w-full flex-1 lg:min-w-[12rem]">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex rounded-[8px] px-2.5 py-1 text-[12px] font-bold ${influencerPaymentStatusClass(
                        item.status
                      )}`}
                    >
                      {influencerPaymentStatusLabel(item.status)}
                    </span>
                  </div>

                  <h3 className="mt-2.5 break-keep text-[17px] font-semibold leading-[1.4] tracking-[-0.03em] text-[#3f4650] md:text-[19px]">
                    {nicknameLabel}
                  </h3>

                  <p className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[14px]">
                    <span className="shrink-0 font-medium text-[#334155]">
                      {title}
                    </span>
                  </p>

                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-[#8a929d]">
                    {item.paid_at ? <span>지급일 {item.paid_at}</span> : null}
                  </div>
                </div>

                <div className="flex w-full items-center justify-between gap-4 border-t border-[#edf0f3] pt-4 lg:w-auto lg:flex-none lg:justify-end lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
                  <div className="min-w-0 flex-1 text-left lg:w-[220px] lg:flex-none lg:text-right">
                    <div className="flex items-center gap-1 text-[#9aa1aa] lg:justify-end">
                      <span className="text-[12px] font-medium leading-snug">
                        실지급액
                      </span>
                    </div>
                    <span className="mt-2.5 block text-[18px] font-bold tabular-nums text-[#111827] sm:text-[20px]">
                      {formatCurrency(
                        parseApiAmount(item.net_amount),
                        currency
                      )}
                    </span>
                    <span className="mt-1 block text-[11px] leading-snug text-[#b0b8c1] tabular-nums">
                      리워드 {formatCurrency(settlementAmount, currency)}
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-[#b0b8c1] tabular-nums">
                      원천징수 {formatCurrency(taxAmount, currency)}
                    </span>
                  </div>
                  <FiChevronRight
                    className="h-[18px] w-[18px] shrink-0 text-[#94a3b8] transition-transform group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Pagination
        current={page}
        total={total}
        pageSize={20}
        onChange={(page) => setPage(page)}
        showSizeChanger={false}
        className="mt-12 justify-center"
      />
    </div>
  );
};
